import { supabase, handleError, type ServiceResult, type PaginatedResult } from '@/services/base.service';
import notificationService from '@/services/notification.service';
import type {
  Company, SubscriptionPlan, CompanySubscription, FeatureFlag,
  UsageSummary, SubscriptionPlanCode,
} from '../types';

export const companiesService = {
  async list(filters: { search?: string; plan?: string; page?: number; pageSize?: number } = {}): Promise<PaginatedResult<Company>> {
    try {
      let query = supabase
        .from('companies')
        .select('*', { count: 'exact' })
        .order('name');
      
      if (filters.plan) query = query.eq('subscription_plan_code', filters.plan);

      const { data, error, count } = await query;
      if (error) throw error;

      let companies = (data ?? []) as Company[];

      // Enriched filtering for search (Supabase doesn't support easy case-insensitive ILIKE on multiple columns without complex query)
      if (filters.search) {
        const search = filters.search.toLowerCase();
        companies = companies.filter(c => 
          c.name.toLowerCase().includes(search) || 
          c.code.toLowerCase().includes(search) ||
          c.email?.toLowerCase().includes(search)
        );
      }

      // Enrich with current usage counts
      const ids = companies.map((c) => c.id);
      if (ids.length === 0) return { data: [], count: 0, error: null };

      const [projRes, userRes, assetRes, subRes] = await Promise.all([
        supabase.from('projects').select('company_id').in('company_id', ids),
        supabase.from('user_profiles').select('company_id').in('company_id', ids),
        supabase.from('landscape_assets').select('company_id').in('company_id', ids),
        supabase.from('company_subscriptions').select('company_id, end_date, status_code').in('company_id', ids).eq('status_code', 'active'),
      ]);

      const projCountMap: Record<string, number> = {};
      (projRes.data ?? []).forEach((p: { company_id: string }) => {
        if (p.company_id) projCountMap[p.company_id] = (projCountMap[p.company_id] ?? 0) + 1;
      });
      
      const userCountMap: Record<string, number> = {};
      (userRes.data ?? []).forEach((u: { company_id: string }) => {
        if (u.company_id) userCountMap[u.company_id] = (userCountMap[u.company_id] ?? 0) + 1;
      });

      const assetCountMap: Record<string, number> = {};
      (assetRes.data ?? []).forEach((a: { company_id: string }) => {
        if (a.company_id) assetCountMap[a.company_id] = (assetCountMap[a.company_id] ?? 0) + 1;
      });
      
      const subMap: Record<string, string | null> = {};
      (subRes.data ?? []).forEach((s: { company_id: string; end_date: string | null }) => {
        subMap[s.company_id] = s.end_date;
      });

      const enriched: Company[] = companies.map((c) => ({
        ...c,
        current_projects: projCountMap[c.id] ?? 0,
        current_users: userCountMap[c.id] ?? 0,
        current_assets: assetCountMap[c.id] ?? 0,
        subscription_end_date: subMap[c.id] ?? null,
      }));

      return { data: enriched, count: count ?? 0, error: null };
    } catch (err) { return { data: [], count: 0, error: handleError(err) }; }
  },

  async getById(id: string): Promise<ServiceResult<Company>> {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return { data: data as Company, error: null };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },

  async upsert(payload: Partial<Company>): Promise<ServiceResult<{ id: string }>> {
    try {
      const { data, error } = await supabase
        .from('companies')
        .upsert({ ...payload, updated_at: new Date().toISOString() })
        .select('id').maybeSingle();
      if (error) throw error;
      return { data: data as { id: string }, error: null };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },

  async delete(id: string): Promise<ServiceResult<void>> {
    try {
      const { error } = await supabase.from('companies').delete().eq('id', id);
      if (error) throw error;
      return { data: null, error: null };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },
  
  async lock(id: string, reason: string): Promise<ServiceResult<void>> {
    try {
      const { error } = await supabase
        .from('companies')
        .update({ 
          is_locked: true, 
          locked_reason: reason, 
          locked_at: new Date().toISOString() 
        })
        .eq('id', id);
      if (error) throw error;
      return { data: null, error: null };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },

  async unlock(id: string): Promise<ServiceResult<void>> {
    try {
      const { error } = await supabase
        .from('companies')
        .update({ 
          is_locked: false, 
          locked_reason: null, 
          locked_at: null 
        })
        .eq('id', id);
      if (error) throw error;
      return { data: null, error: null };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },
  
  async activate(id: string, options: { 
    planCode?: SubscriptionPlanCode;
    endDate?: string;
    approvedBy?: string;
  } = {}): Promise<ServiceResult<void>> {
    try {
      // 1. Get company details for initial checks
      const { data: company } = await supabase
        .from('companies')
        .select('name, owner_user_id, subscription_plan_code')
        .eq('id', id)
        .single();

      // 2. Prepare update payload
      const updates: any = { 
        is_active: true,
        activated_at: new Date().toISOString(),
        approved_by: options.approvedBy
      };

      if (options.planCode) {
        updates.subscription_plan_code = options.planCode;
      }

      // 3. Activate company
      const { error: companyError } = await supabase
        .from('companies')
        .update(updates)
        .eq('id', id);
      if (companyError) throw companyError;

      // 4. Activate owner profile if exists and set as Admin
      if (company?.owner_user_id) {
        await supabase
          .from('user_profiles')
          .update({ 
            is_active: true,
            role_code: 'admin' // Set as Tenant Admin upon activation
          })
          .eq('id', company.owner_user_id);
      }

      // 5. Create/Update subscription entry
      if (options.endDate || options.planCode) {
        await supabase
          .from('company_subscriptions')
          .insert({
            company_id: id,
            plan_code: options.planCode || company?.subscription_plan_code || 'free',
            status_code: 'active',
            start_date: new Date().toISOString(),
            end_date: options.endDate,
            billing_cycle: 'monthly',
            amount_paid: 0,
            notes: 'Activated by Super Admin'
          });
      }

      // 6. Send notification to owner
      if (company?.owner_user_id) {
        await notificationService.create({
          user_id: company.owner_user_id,
          title: 'Tổ chức đã được kích hoạt',
          message: `Chào mừng! Tổ chức "${company.name || 'của bạn'}" đã được phê duyệt và kích hoạt gói ${options.planCode || company.subscription_plan_code}.`,
          link: '/dashboard'
        });
      }

      return { data: null, error: null };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },

  async getUsageSummary(companyId: string): Promise<ServiceResult<UsageSummary>> {
    try {
      const today = new Date();
      const periodDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;

      const { data } = await supabase
        .from('usage_tracking')
        .select('metric_key, metric_value')
        .eq('company_id', companyId)
        .eq('period_date', periodDate);

      const metricMap: Record<string, number> = {};
      (data ?? []).forEach((d: { metric_key: string; metric_value: number }) => {
        metricMap[d.metric_key] = d.metric_value;
      });

      // Fallback to live count if not tracked yet
      const [projRes, userRes, assetRes, woRes, logRes] = await Promise.all([
        supabase.from('projects').select('id', { count: 'exact', head: true }).eq('company_id', companyId),
        supabase.from('user_profiles').select('id', { count: 'exact', head: true }).eq('company_id', companyId),
        supabase.from('landscape_assets').select('id', { count: 'exact', head: true }).eq('company_id', companyId),
        supabase.from('work_orders').select('id', { count: 'exact', head: true }).eq('company_id', companyId),
        supabase.from('maintenance_logs').select('id', { count: 'exact', head: true }).eq('company_id', companyId),
      ]);

      return {
        data: {
          projects_count: projRes.count ?? metricMap['projects_count'] ?? 0,
          users_count: userRes.count ?? metricMap['users_count'] ?? 0,
          assets_count: assetRes.count ?? metricMap['assets_count'] ?? 0,
          work_orders_count: woRes.count ?? metricMap['work_orders_count'] ?? 0,
          logs_count: logRes.count ?? metricMap['logs_count'] ?? 0,
          storage_mb: metricMap['storage_mb'] ?? 0,
        },
        error: null,
      };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },

  async getSubscriptions(companyId: string): Promise<ServiceResult<CompanySubscription[]>> {
    try {
      const { data, error } = await supabase
        .from('company_subscriptions')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return { data: data as CompanySubscription[], error: null };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },

  async getProjects(companyId: string): Promise<ServiceResult<any[]>> {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('company_id', companyId)
        .order('name');
      if (error) throw error;
      return { data, error: null };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },

  async getUsers(companyId: string): Promise<ServiceResult<any[]>> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('company_id', companyId)
        .order('full_name');
      if (error) throw error;
      return { data, error: null };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },

  async getOwnerProfile(companyId: string): Promise<ServiceResult<any>> {
    try {
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('owner_user_id')
        .eq('id', companyId)
        .single();
      if (companyError) throw companyError;
      if (!company?.owner_user_id) throw new Error('Company has no owner assigned');

      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', company.owner_user_id)
        .single();
      if (profileError) throw profileError;

      return { data: profile, error: null };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },

  async getUsageHistory(companyId: string): Promise<ServiceResult<Array<{ period_date: string; [key: string]: number | string }>>> {
    try {
      const { data, error } = await supabase
        .from('usage_tracking')
        .select('metric_key, metric_value, period_date')
        .eq('company_id', companyId)
        .order('period_date', { ascending: true })
        .limit(60);
      if (error) throw error;

      // Pivot: group by period_date
      const pivot: Record<string, { period_date: string; [key: string]: number | string }> = {};
      (data ?? []).forEach((d: { metric_key: string; metric_value: number; period_date: string }) => {
        if (!pivot[d.period_date]) pivot[d.period_date] = { period_date: d.period_date };
        pivot[d.period_date][d.metric_key] = d.metric_value;
      });

      return { data: Object.values(pivot), error: null };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },
};

export const plansService = {
  async list(): Promise<ServiceResult<SubscriptionPlan[]>> {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('display_order');
      if (error) throw error;
      return {
        data: (data ?? []).map((p) => ({
          ...p,
          features: Array.isArray(p.features) ? p.features : [],
        })) as SubscriptionPlan[],
        error: null,
      };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },
};

export const featureFlagService = {
  async getByPlan(planCode: string): Promise<ServiceResult<FeatureFlag[]>> {
    try {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('*')
        .eq('plan_code', planCode);
      if (error) throw error;
      return { data: (data ?? []) as FeatureFlag[], error: null };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },

  async getAllFlags(): Promise<ServiceResult<FeatureFlag[]>> {
    try {
      const { data, error } = await supabase.from('feature_flags').select('*').order('plan_code').order('feature_key');
      if (error) throw error;
      return { data: (data ?? []) as FeatureFlag[], error: null };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },

  async toggle(planCode: string, featureKey: string, isEnabled: boolean): Promise<ServiceResult<void>> {
    try {
      const { error } = await supabase
        .from('feature_flags')
        .update({ is_enabled: isEnabled })
        .eq('plan_code', planCode)
        .eq('feature_key', featureKey);
      if (error) throw error;
      return { data: null, error: null };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },
};

export const companySubscriptionService = {
  async getByCompany(companyId: string): Promise<ServiceResult<CompanySubscription[]>> {
    try {
      const { data, error } = await supabase
        .from('company_subscriptions')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return { data: (data ?? []) as CompanySubscription[], error: null };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },

  async create(payload: Omit<CompanySubscription, 'id' | 'created_at'>): Promise<ServiceResult<{ id: string }>> {
    try {
      const { data, error } = await supabase
        .from('company_subscriptions')
        .insert(payload)
        .select('id')
        .maybeSingle();
      if (error) throw error;
      return { data: data as { id: string }, error: null };
    } catch (err) { return { data: null, error: handleError(err) }; }
  },
};
